'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import {
  isCcsdCodeQuery,
  isCcsdCodeSearchPending,
  SPEC_OPTIONS,
} from '@/features/search';

import SearchBarShell from './components/SearchBarShell';
import SearchResultsDropdown from './components/SearchResultsDropdown';
import SpecialityFilter from './SpecialityFilter';
import {
  useAutocompleteKeyboardNav,
  useCcsdSearchEnabled,
  useDismissOnOutsideClick,
  useIsMobile,
} from './hooks';
import { getOptionId } from './types';
import type { HeroSearchBarProps } from './types';
import { useAutocompleteSearch } from './useAutocompleteSearch';

export type { HeroSearchBarProps } from './types';
export type {
  AutocompleteSuggestion,
  BlogSuggestion,
  DoctorSuggestion,
  SpecialitySuggestion,
} from './types';

const MIN_QUERY_LENGTH = 2;

const HeroSearchBar: React.FC<HeroSearchBarProps> = ({
  inputPlaceholder = 'Search by Name, Speciality or Condition....',
  searchButtonLabel = 'Search',
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSpecialities, setSelectedSpecialities] = useState<string[]>(
    [],
  );
  const [openSpecialityDropdown, setOpenSpecialityDropdown] = useState(false);
  const [hoveredSpecialitySlug, setHoveredSpecialitySlug] = useState<
    string | null
  >(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const ccsdSearchEnabled = useCcsdSearchEnabled();
  const isMobile = useIsMobile();
  const { suggestions, isLoading } = useAutocompleteSearch({
    searchTerm,
    selectedSpecialities,
  });
  // ─── Derived view state ───────────────────────────────────────────────────
  const selectedSpecTitle =
    selectedSpecialities.length > 0
      ? selectedSpecialities.length === 1
        ? SPEC_OPTIONS.find((opt) => opt.slug === selectedSpecialities[0])
            ?.title || selectedSpecialities[0]
        : `Speciality (${selectedSpecialities.length})`
      : 'Speciality';
  const trimmedTerm = searchTerm.trim();
  const isCodeSearch = ccsdSearchEnabled && isCcsdCodeQuery(trimmedTerm);
  const dropdownVisible =
    showDropdown &&
    trimmedTerm.length >= MIN_QUERY_LENGTH &&
    !isCcsdCodeSearchPending(trimmedTerm);
  // ─── Search submission + suggestion navigation ────────────────────────────
  const closeAll = useCallback(() => {
    setShowDropdown(false);
    setOpenSpecialityDropdown(false);
    setHoveredSpecialitySlug(null);
  }, []);
  const navigate = useCallback((url: string) => {
    window.location.href = url;
  }, []);
  const submitSearch = useCallback(() => {
    const trimmed = searchTerm.trim();
    const params = new URLSearchParams();
    if (trimmed) params.set('q', trimmed);
    if (selectedSpecialities.length > 0)
      params.set('speciality', selectedSpecialities.join(','));
    const query = params.toString();
    closeAll();
    window.location.href = query
      ? `/our-specialists?${query}`
      : '/our-specialists';
  }, [searchTerm, selectedSpecialities, closeAll]);
  const selectSuggestion = useCallback(
    (index: number) => {
      const target = suggestions[index];
      if (target) navigate(target.url);
    },
    [suggestions, navigate],
  );
  // ─── Keyboard navigation + dismissal ──────────────────────────────────────
  const { activeIndex, setActiveIndex, handleKeyDown } =
    useAutocompleteKeyboardNav({
      suggestionCount: suggestions.length,
      dropdownRef,
      onSelectSuggestion: selectSuggestion,
      onSubmitSearch: submitSearch,
      onClose: closeAll,
      enabled: showDropdown,
    });
  useDismissOnOutsideClick({ containerRef, onClose: closeAll });
  useEffect(() => {
    setActiveIndex(-1);
  }, [suggestions, setActiveIndex]);
  useEffect(() => {
    if (!showDropdown) setActiveIndex(-1);
  }, [showDropdown, setActiveIndex]);

  // ─── Scroll search bar to the top of the viewport on focus ────────────────
  // Gives the user a clear view of the results dropdown on desktop, tablet
  // and mobile. Accounts for the sticky header height and is a no-op when
  // the bar is already in position (prevents jumpy re-scrolls).
  const scrollSearchBarIntoView = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const HEADER_OFFSET = 80;
    const rect = el.getBoundingClientRect();
    const targetY = window.scrollY + rect.top - HEADER_OFFSET;
    if (Math.abs(window.scrollY - targetY) > 4) {
      window.scrollTo({ top: targetY, behavior: 'smooth' });
    }
  }, []);

  const handleInputFocus = useCallback(() => {
    setShowDropdown(true);
    scrollSearchBarIntoView();
  }, [scrollSearchBarIntoView]);
  // ─── Local input + filter handlers ────────────────────────────────────────
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setShowDropdown(true);
    setHoveredSpecialitySlug(null);
    setActiveIndex(-1);
  };
  const toggleSpeciality = (slug: string) => {
    setSelectedSpecialities((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug],
    );
  };

  // ─── Search box + results + filter panels ─────────────────────────────────
  return (
    <div
      ref={containerRef}
      className="xl:w-searchbar-wrapper-width relative z-9999 w-full max-w-3xl xl:max-w-none"
    >
      <SearchBarShell
        searchTerm={searchTerm}
        inputPlaceholder={inputPlaceholder}
        searchButtonLabel={searchButtonLabel}
        selectedSpecTitle={selectedSpecTitle}
        showDropdown={showDropdown}
        suggestionCount={suggestions.length}
        activeDescendantId={
          activeIndex >= 0 && suggestions[activeIndex]
            ? getOptionId(suggestions[activeIndex])
            : undefined
        }
        openSpecialityDropdown={openSpecialityDropdown}
        onInputChange={handleInputChange}
        onInputFocus={handleInputFocus}
        onKeyDown={handleKeyDown}
        onToggleSpecialityDropdown={() => setOpenSpecialityDropdown((v) => !v)}
        onSearch={submitSearch}
      />
      {dropdownVisible && (
        <SearchResultsDropdown
          searchTerm={searchTerm}
          suggestions={suggestions}
          isLoading={isLoading}
          isCodeSearch={isCodeSearch}
          activeIndex={activeIndex}
          hoveredSpecialitySlug={hoveredSpecialitySlug}
          dropdownRef={dropdownRef}
          onHoverSpeciality={(slug, flatIndex) => {
            setActiveIndex(flatIndex);
            setHoveredSpecialitySlug(slug);
          }}
          onHoverRow={(flatIndex) => setActiveIndex(flatIndex)}
          onClearHover={() => setHoveredSpecialitySlug(null)}
          onSelect={navigate}
        />
      )}
      <SpecialityFilter
        isMobile={isMobile}
        open={openSpecialityDropdown}
        selectedSpecialities={selectedSpecialities}
        onToggle={toggleSpeciality}
        onClear={() => setSelectedSpecialities([])}
        onClose={() => setOpenSpecialityDropdown(false)}
      />
    </div>
  );
};
export default HeroSearchBar;
